'use strict'

const isEqual_ = require('lodash/isEqual')
const isBoolean_ = require('lodash/isBoolean')

const isFalse = val => isEqual_(false, val)

const {
  getCurrentPageSize,
  getPaginationData,
  selectCurrentRecordIndex,
  selectCurrentRecord,
  selectDefaultDraft
} = require('../dataset-controller/rootReducer')
const actionTypes = require('./actionTypes')
const { setIsDuringSave } = require('../dataset-config/actions')
const DatasetError = require('../dataset-controller/DatasetError')
const isComponentValid = require('../helpers/isComponentValid')
const queue = require('../actions-queue/addToQueue')
const initAdapters = require('../adapters/initAdapters')
const queryResults = require('../helpers/queryResults')

const setCurrentIndex = (index, suppressRefreshView = false) => ({
  type: actionTypes.GO_TO_INDEX,
  index,
  suppressRefreshView
})

const refreshCurrentRecord = () => ({
  type: actionTypes.REFRESH_CURRENT_RECORD
})

const refreshCurrentView = () => ({
  type: actionTypes.REFRESH_CURRENT_VIEW
})

const resetRefreshCurrentView = () => ({
  type: actionTypes.RESET_REFRESH_CURRENT_VIEW
})

const setCurrentRecord = (record, recordIndex) => ({
  type: actionTypes.SET_CURRENT_RECORD,
  record,
  recordIndex
})

const updateFields = (fieldsToUpdate, updateSource) => ({
  type: actionTypes.UPDATE_FIELDS,
  fieldsToUpdate,
  updateSource
})

const incrementNumOfPagesToShow = () => ({
  type: actionTypes.INCREMENT_NUM_PAGES_TO_SHOW
})

const nextPage = () => ({
  type: actionTypes.GO_TO_NEXT_PAGE
})

const previousPage = () => ({
  type: actionTypes.GO_TO_PREVIOUS_PAGE
})

const doFetch = async (recordStore, fromIndex, length) => {
  const fetchedItems = await recordStore().matchWith({
    Null: () => queryResults.Empty(),
    Pending: () => queryResults.Empty(),
    Ready: async ({ instance }) => {
      const queryResult = await instance.getRecords(fromIndex, length)

      return queryResult
    }
  })

  return fetchedItems.get()
}

const fetch = (fromIndex, length) => (dispatch, getState, { recordStore }) =>
  doFetch(recordStore, fromIndex, length)

const fetchCurrentPage = (recordStore, state) => {
  const pageSize = getCurrentPageSize(state)
  const { offset } = getPaginationData(state)

  return doFetch(recordStore, offset, pageSize)
}

const assertComponentsValid = (componentAdapterContexts, currentRecord) => {
  const nonValidComponentAdapterContexts = componentAdapterContexts.filter(
    cac => !isComponentValid(cac, currentRecord)
  )
  nonValidComponentAdapterContexts.forEach(({ component }) =>
    component.updateValidityIndication()
  )

  if (nonValidComponentAdapterContexts.length) {
    throw new DatasetError(
      'DS_VALIDATION_ERROR',
      'Some of the elements validation failed'
    )
  }
}

const updateComponentsBasedOnRecord = (
  componentIdToExcludeFromUpdatingComponentsBasedOnRecord,
  updatedFields
) => (dispatch, getState, { adapterApi }) => {
  adapterApi().currentRecordModified(
    updatedFields,
    componentIdToExcludeFromUpdatingComponentsBasedOnRecord
  )
}
exports.updateComponentsBasedOnRecord = updateComponentsBasedOnRecord

const executeBeforeSaveHooks = executeHooks => {
  return executeHooks('beforeSave')
    .then(hookResults => hookResults.some(isFalse))
    .catch(e => ({ error: e }))
    .then(shouldCancelSave => {
      if (shouldCancelSave) {
        throw new DatasetError(
          'DS_OPERATION_CANCELLED',
          `Operation cancelled by user code. ${
            isBoolean_(shouldCancelSave) ? '' : shouldCancelSave.error
          }`
        )
      }
    })
}

const performSave = async function({ dispatch, getState, recordStore }) {
  const currentRecordIndex = selectCurrentRecordIndex(getState())

  const postSaveRecord = await recordStore().matchWith({
    Ready: ({ instance }) => instance.saveRecord(currentRecordIndex)
  })

  return postSaveRecord
}

const flushDraftThunk = async function(
  dispatch,
  getState,
  {
    recordStore,
    componentAdapterContexts,
    adapterApi,
    eventListeners: { fireEvent, executeHooks },
    getFieldType
  }
) {
  dispatch(setIsDuringSave(true))

  try {
    await executeBeforeSaveHooks(executeHooks)

    const hasDraft = recordStore().matchWith({
      Pending: () => false,
      Ready: ({ instance }) =>
        instance.hasDraft(selectCurrentRecordIndex(getState()))
    })
    if (!hasDraft) {
      return selectCurrentRecord(getState())
    }

    assertComponentsValid(
      componentAdapterContexts,
      selectCurrentRecord(getState())
    )

    const postSaveRecord = await performSave({
      dispatch,
      getState,
      recordStore
    })
    fireEvent('afterSave')

    return postSaveRecord
  } finally {
    dispatch(setIsDuringSave(false))
  }
}

exports.fetchInitialDataAndSetInitialCurrentRecord = (
  hasPrefetchedData,
  errorReporter
) => {
  if (hasPrefetchedData) {
    return setInitialCurrentRecord
  }

  return queue(fetchInitialDataAndSetInitialCurrentRecord(errorReporter))
}

const setInitialCurrentRecord = (dispatch, getState, { recordStore }) =>
  recordStore().matchWith({
    Null: () => Promise.resolve(),
    Pending: () => Promise.resolve(),
    Ready: ({ instance }) =>
      instance
        .getSeedRecords()
        .chain(({ items }) => dispatch(setCurrentRecord(items[0], 0)))
  })

const fetchInitialDataAndSetInitialCurrentRecord = errorReporter => (
  dispatch,
  getState,
  { recordStore, fedopsLogger }
) => {
  const { timeoutId } = fedopsLogger.interactionStarted('pageReady/getData')
  return recordStore().matchWith({
    Null: () => Promise.resolve(),
    Pending: () => Promise.resolve(),
    Ready: ({ instance }) =>
      instance
        .seed()
        .then(() =>
          instance
            .getRecords(0, 1)
            .then(queryResult =>
              queryResult.chain(({ items }) =>
                dispatch(setCurrentRecord(items[0], 0))
              )
            )
            .then(() =>
              fedopsLogger.interactionEnded('pageReady/getData', { timeoutId })
            )
        )
        .catch(e => errorReporter('Failed to load initial data', e))
  })
}

exports.initWriteOnly = () =>
  queue(async function(dispatch, getState, { recordStore }) {
    await recordStore().matchWith({
      Pending: () => {
        throw new Error('Record store service not ready yet')
      },
      Ready: async ({ instance }) => {
        instance.newRecord(0, selectDefaultDraft(getState()))
        await dispatch(setCurrentIndex(0))
      }
    })
  })

exports.reInitWriteOnly = () =>
  queue(async function(dispatch, getState, { recordStore }) {
    dispatch(newRecord(0))
  })

const initAdaptersThunk = (dispatch, getState, { adapterApi }) => {
  return initAdapters(adapterApi)
}

exports.initAdapters = hasPrefetchedData =>
  hasPrefetchedData ? initAdaptersThunk : queue(initAdaptersThunk)

exports.refresh = () =>
  queue(async function(
    dispatch,
    getState,
    { adapterApi, eventListeners: { fireEvent }, recordStore }
  ) {
    await recordStore().matchWith({
      Pending: () => {},
      Ready: async ({ instance }) => {
        instance.resetDraft(
          selectCurrentRecordIndex(getState()),
          selectDefaultDraft(getState())
        )
        instance.reset()
        await dispatch(setCurrentIndex(0, true))
        await Promise.all(adapterApi().recordSetLoaded())

        fireEvent('currentIndexChanged', 0)
      }
    })
  })

const newRecord = atIndex =>
  queue(async function(dispatch, getState, { adapterApi, recordStore }) {
    await recordStore().matchWith({
      Pending: () => {
        throw new Error('Record store service not ready yet')
      },
      Ready: async ({ instance }) => {
        const newRecord = instance.newRecord(
          atIndex,
          selectDefaultDraft(getState())
        )
        dispatch(setCurrentRecord(newRecord, atIndex))
      }
    })
  })

exports.newRecord = newRecord

exports.remove = () =>
  queue(async function(dispatch, getState, { recordStore }) {
    const index = selectCurrentRecordIndex(getState())

    await recordStore().matchWith({
      Pending: () => {
        throw new Error('Record store service not ready yet')
      },
      Ready: async ({ instance }) => {
        await instance.removeRecord(index)
        return await dispatch(refreshCurrentRecord())
      }
    })
  })

exports.flushDraft = () => queue(flushDraftThunk)

exports.revert = () => (dispatch, getState, services) => {
  const state = getState()
  services.recordStore().matchWith({
    Pending: () => {},
    Ready: ({ instance }) =>
      instance.resetDraft(
        selectCurrentRecordIndex(state),
        selectDefaultDraft(state)
      )
  })

  // This updateComponentsBasedOnRecord is still needed because of upload buttons' behaviour.
  // Upload buttons, when a file is selected, do not update the record data, but still
  // need to be reverted to clear that selection. Therefore, the revert action must always
  // call the adapters' currentRecordModified event.
  dispatch(updateComponentsBasedOnRecord(undefined))
}

exports.fetch = (fromIndex, length) => queue(fetch(fromIndex, length))
exports.fetchCurrentPage = fetchCurrentPage
exports.doFetch = doFetch

exports.setDefaultRecord = record => ({
  type: actionTypes.SET_DEFAULT_RECORD,
  record
})

exports.setCurrentIndex = setCurrentIndex
exports.incrementNumOfPagesToShow = incrementNumOfPagesToShow
exports.setCurrentRecord = setCurrentRecord
exports.updateFields = updateFields
exports.nextPage = nextPage
exports.previousPage = previousPage
exports.refreshCurrentView = refreshCurrentView
exports.resetRefreshCurrentView = resetRefreshCurrentView
exports.refreshCurrentRecord = refreshCurrentRecord



// WEBPACK FOOTER //
// ./records/actions.js